import { useDispatch } from 'react-redux';
import { PaginationData } from 'br2s-ui-componentlibrary_r2_02';
import { reportDtCustomError, useHTTPError } from 'br2s-ui-common-utils-02';
import { PROFILE_TYPE_KEY_MAP } from '@/pages/ProfileSelection/profileSelection-constant';
import {
  getProfiles,
  generateAvailableTabKeys,
  hasNonEmptyProfileRecords,
  flattenMyEntities,
} from '@/pages/ProfileSelection/profileSelection-utilities';
import {
  toggleProfileSwitchLoading,
  setPagination,
  saveProfileRecords,
  setTabKeys,
  setActiveTab,
  resetProfileSwitchState,
  toggleTableLoad,
  saveSingleProfileTypeRecord,
  ProfileEntitiesObject,
  setFetched,
  setSelectedEntity,
  ProfileTypeKeys,
  setSearchValue,
  ProfileRecord,
  ProfilePagination,
} from '@/redux/slices/profileSwitchSlice';
import { useAppSelector } from '@/redux/store';
import { AuthAPI } from '@/services/api/auth';

const useProfileSwitch = () => {
  const { fetched, loading, entities, uiState } = useAppSelector(
    (state) => state.profileSwitch,
  );
  const selectedTabKey = uiState.tabKeys[uiState.tabIndex];

  const dispatch = useDispatch();
  const { handleHTTPError } = useHTTPError();

  /**
   * Method to asynchronously call 5 profile APIs for switch profile
   * @param onSuccess Optional callback function invoked after successful profile fetch. Receives the fetched records as an argument.
   */
  const fetchProfiles = async (
    onSuccess?: (t: ProfileEntitiesObject) => void,
  ) => {
    const records: ProfileEntitiesObject = {};

    try {
      dispatch(toggleProfileSwitchLoading(true));
      const { profileTypes, profiles } = getProfiles();
      const responses = await profiles;

      responses.forEach((response, index) => {
        if (response.status === 'fulfilled') {
          const key = PROFILE_TYPE_KEY_MAP[profileTypes[index]];
          const { records: responseRecords } = response.value.data.result;
          records[key] = responseRecords;
        } else {
          handleHTTPError(response.reason, {
            errorType: 'inline',
            inlineAlertKey: `switch-error-${index}`,
          });
        }
      });

      if (responses.some((response) => response.status === 'fulfilled')) {
        dispatch(saveProfileRecords(records));
        dispatch(setTabKeys(generateAvailableTabKeys(records)));
        if (hasNonEmptyProfileRecords(records)) dispatch(setActiveTab(0));
        if (records.myEntities) {
          dispatch(
            setPagination({
              key: 'myEntities',
              value: { items: flattenMyEntities(records.myEntities).length },
            }),
          );
        }
      }
    } catch (error) {
      console.error(error);
      handleHTTPError(error);
      reportDtCustomError('GenericHandledError', JSON.stringify(error));
    } finally {
      dispatch(setFetched(true));
      dispatch(toggleProfileSwitchLoading(false));
      onSuccess?.(records);
    }
  };

  /**
   * Method to fetch single profile by selecting dropdown / changing pagination
   * @param selectedUen Selected UEN value of CSP/GC/IP/OR dropdown
   * @param selectedTabKey Key of current tab
   * @param paginationData PaginationData
   * @param searchKey Optional: Search key
   * @param sortOrder Sort order, defaults to 'desc'
   * @param sortBy Sort whether last updated or alphabetical, defaults to 'lastUpdated'
   */
  const fetchProfilesIndividual = async (
    selectedUen: string | null,
    selectedTabKey: ProfileTypeKeys,
    paginationData: Omit<PaginationData, 'items'>,
    searchKey = '',
    sortOrder = 'desc',
    sortBy = 'lastUpdated',
  ) => {
    const profileType =
      Object.keys(PROFILE_TYPE_KEY_MAP).find(
        (key) => PROFILE_TYPE_KEY_MAP[key] === selectedTabKey,
      ) ?? '';

    try {
      dispatch(
        toggleTableLoad({
          key: selectedTabKey,
          value: true,
        }),
      );
      const { page, itemsPerPage } = paginationData;
      const response = await AuthAPI.switchProfile({
        profileType,
        pageNumber: page,
        pageSize: itemsPerPage,
        sortOrder,
        sortBy,
        ...(searchKey ? { searchKey } : {}),
        ...(selectedUen ? { selectedUen } : {}),
      });
      const {
        records,
        pageNumber,
        totalRecords,
        pageSize,
        sortBy: responseSortBy,
        sortOrder: responseSortOrder,
      } = response.data.result;
      dispatch(
        saveSingleProfileTypeRecord({
          key: selectedTabKey,
          records,
          pagination: {
            pageNumber,
            items: totalRecords,
            pageSize,
            sortBy: responseSortBy,
            sortOrder: responseSortOrder,
          },
        }),
      );
    } catch (error) {
      console.error(error);
      reportDtCustomError('GenericHandledError', JSON.stringify(error));
      handleHTTPError(error, { errorType: 'inline' });
    } finally {
      dispatch(
        toggleTableLoad({
          key: selectedTabKey,
          value: false,
        }),
      );
    }
  };

  return {
    fetched,
    loading,
    entities,
    uiState,
    selectedTabKey,
    fetchProfiles,
    fetchProfilesIndividual,
    toggleTableLoad: (key: ProfileTypeKeys, value: boolean) =>
      dispatch(toggleTableLoad({ key, value })),
    setPagination: (key: ProfileTypeKeys, value: Partial<ProfilePagination>) =>
      dispatch(setPagination({ key, value })),
    saveSingleProfileTypeRecord: (
      key: ProfileTypeKeys,
      records: ProfileRecord[],
      pagination: Partial<ProfilePagination>,
    ) =>
      dispatch(
        saveSingleProfileTypeRecord({ key, records, pagination: pagination }),
      ),
    setSearchValue: (key: ProfileTypeKeys, value: string) =>
      dispatch(setSearchValue({ key, value })),
    setSelectedEntity: (
      key: Exclude<ProfileTypeKeys, 'myEntities' | 'officialReceiver'>,
      value: string,
    ) => dispatch(setSelectedEntity({ key, value })),
    setActiveTab: (index: number) => dispatch(setActiveTab(index)),
    resetProfileSwitchState: () => dispatch(resetProfileSwitchState()),
  };
};

export default useProfileSwitch;
